/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';

/**
 * Hook para manejar secciones colapsables con altura dinámica.
 * Funciona tanto en modo de sección única como múltiple.
 *
 * @param {Object} options - Opciones de configuración
 * @param {number} [options.visibleItems=5] - Número de elementos visibles cuando está colapsada
 * @param {string} [options.itemSelector='.item'] - Selector CSS para identificar los elementos
 * @param {Array} [options.dependencies=[]] - Dependencias para recalcular las alturas
 * @param {Array} [options.sections] - IDs de las secciones (si no se pasa, modo sección única)
 * @param {boolean} [options.initialCollapse=false] - Si las secciones deben iniciar colapsadas
 *
 * @returns {Object} Métodos para controlar las secciones colapsables
 *
 *  *
 * Ejemplo de uso para varias secciones:
 *
 * const { registerRef, getMaxHeight, isCollapsed, setCollapsed } = useSectionsCollapse({
 *   visibleItems: 3,
 *   itemSelector: '.item',
 *   dependencies: [data],
 *   sections: ['servicios', 'amenidades', 'exteriores'],
 *   initialCollapse: false
 * });
 *
 * // En el render:
 * <div ref={el => registerRef('servicios', el)}>...</div>
 * <div ref={el => registerRef('amenidades', el)}>...</div>
 * <div ref={el => registerRef('exteriores', el)}>...</div>
 *
 * // Para obtener la altura máxima de una sección:
 * getMaxHeight('servicios')
 *
 * // Para saber si una sección está colapsada:
 * isCollapsed('servicios')
 *
 * // Para colapsar/expandir una sección:
 * setCollapsed('servicios', true) // colapsar
 * setCollapsed('servicios', false) // expandir
 *
 * Para modo de sección única, omite el prop 'sections' y usa solo registerRef(elemento).
 */

const useSectionsCollapse = ({
  visibleItems = 5,
  itemSelector = '.item',
  dependencies = [],
  sections,
  initialCollapse = false
}) => {
  // Determinar si se está usando en modo de sección única. Para usar modo de sección única no se debe pasar 'sections'
  const singleSectionMode = !sections;

  // Normalizar secciones para el procesamiento interno
  const normalizedSections = singleSectionMode ? ['default'] : sections || [];
  const defaultSectionId = 'default';

  // Referencias a los contenedores de secciones y tiempo de resize
  const refs = useRef({
    sections: {},
    lastCalculationKey: '',
    resizeTimeout: null
  });

  // Estado para almacenar información de colapso de cada sección
  const [sectionsState, setSectionsState] = useState(() =>
    normalizedSections.reduce((acc, sectionId) => {
      acc[sectionId] = {
        collapsed: initialCollapse,
        height: { collapsed: '0px', expanded: '0px' }
      };
      return acc;
    }, {})
  );

  // Función para calcular las alturas de un sólo contenedor
  const calculateHeights = container => {
    if (!container) return { collapsed: '0px', expanded: '0px' };

    const elements = Array.from(container.querySelectorAll(itemSelector));
    const expandedHeight = `${container.scrollHeight}px`;

    // Si no hay suficientes elementos para colapsar, usar la altura completa
    if (!elements || elements.length <= visibleItems) {
      return { collapsed: expandedHeight, expanded: expandedHeight };
    }

    const lastVisibleItem = elements[visibleItems - 1];
    if (!lastVisibleItem) return { collapsed: expandedHeight, expanded: expandedHeight };

    // Calcular altura colapsada basada en la posición del último elemento visible
    const lastItemBottom = lastVisibleItem.getBoundingClientRect().bottom;
    const containerTop = container.getBoundingClientRect().top;
    const collapsedHeight = `${lastItemBottom - containerTop}px`;

    return { collapsed: collapsedHeight, expanded: expandedHeight };
  };

  // Función para actualizar las alturas de todas las secciones
  const updateAllHeights = () => {
    // Creamos una clave única para esta actualización basada en las dependencias
    const calculationKey = JSON.stringify(dependencies);

    // Solo actualizamos si hay cambios en las dependencias o es la primera vez
    if (calculationKey !== refs.current.lastCalculationKey) {
      refs.current.lastCalculationKey = calculationKey;

      setSectionsState(prevState => {
        const newState = { ...prevState };
        let hasChanges = false;

        // Actualizar cada sección registrada
        normalizedSections.forEach(sectionId => {
          const container = refs.current.sections[sectionId];
          if (container) {
            const newHeights = calculateHeights(container);

            // Verificar si la sección ya existe y si hay cambios en las alturas
            const sectionExists = Boolean(prevState[sectionId]);
            const heightsChanged =
              sectionExists &&
              (newHeights.collapsed !== prevState[sectionId].height.collapsed ||
                newHeights.expanded !== prevState[sectionId].height.expanded);

            // Actualizar si la sección no existe o las alturas cambiaron
            if (!sectionExists || heightsChanged) {
              newState[sectionId] = {
                ...(prevState[sectionId] || { collapsed: initialCollapse }),
                height: newHeights
              };
              hasChanges = true;
            }
          }
        });

        // Solo retornamos un nuevo estado si realmente hay cambios
        return hasChanges ? newState : prevState;
      });
    }
  };

  // Registrar referencia para una sección
  const registerRef = (sectionIdOrElement, element) => {
    if (singleSectionMode) {
      // En modo sección única, el primer argumento es el elemento
      refs.current.sections[defaultSectionId] = sectionIdOrElement;
    } else {
      // En modo multi-sección, necesitamos ambos argumentos
      refs.current.sections[sectionIdOrElement] = element;
    }

    // Calcular alturas después de registrar la referencia (con un pequeño retraso)
    setTimeout(updateAllHeights, 0);
  };

  // Obtener altura máxima para una sección
  const getMaxHeight = sectionId => {
    const id = singleSectionMode ? defaultSectionId : sectionId;
    const section = sectionsState[id];

    if (!section) return '0px';
    return section.collapsed ? section.height.collapsed : section.height.expanded;
  };

  // Verificar si una sección está colapsada
  const isCollapsed = sectionId => {
    const id = singleSectionMode ? defaultSectionId : sectionId;
    return sectionsState[id]?.collapsed ?? initialCollapse;
  };

  // Establecer estado de colapso para una sección
  const setCollapsed = (sectionIdOrValue, value) => {
    if (singleSectionMode) {
      // En modo sección única, el primer argumento es el valor o se alterna
      const newValue = value === undefined ? !sectionsState[defaultSectionId]?.collapsed : sectionIdOrValue;

      setSectionsState(prev => ({
        ...prev,
        [defaultSectionId]: {
          ...(prev[defaultSectionId] || { height: { collapsed: '0px', expanded: '0px' } }),
          collapsed: newValue
        }
      }));
    } else {
      // En modo multi-sección
      const sectionId = sectionIdOrValue;
      const newValue = value === undefined ? !sectionsState[sectionId]?.collapsed : value;

      setSectionsState(prev => ({
        ...prev,
        [sectionId]: {
          ...(prev[sectionId] || { height: { collapsed: '0px', expanded: '0px' } }),
          collapsed: newValue
        }
      }));
    }
  };

  // Efecto para calcular alturas iniciales y configurar event listeners
  useEffect(() => {
    // Calcular alturas después del renderizado inicial
    const timeoutId = setTimeout(updateAllHeights, 1);

    // Handler para el evento resize con debounce
    const handleResize = () => {
      clearTimeout(refs.current.resizeTimeout);
      refs.current.resizeTimeout = setTimeout(updateAllHeights, 10);
    };

    window.addEventListener('resize', handleResize);

    // Limpiar timeouts y event listeners
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(refs.current.resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Efecto para recalcular cuando cambian las dependencias o secciones
  useEffect(() => {
    updateAllHeights();
  }, [dependencies, normalizedSections.length]);

  return {
    registerRef,
    getMaxHeight,
    isCollapsed,
    setCollapsed
  };
};

export default useSectionsCollapse;
